import DOMPurify from 'dompurify';

const UNUSUAL_LINE_TERMINATORS = /[\u2028\u2029]/g;

export function normalizeLineTerminators(value = ''): string {
  return value.replace(UNUSUAL_LINE_TERMINATORS, '\n');
}

const DESCRIPTION_FROM_MARKDOWN_CONFIG: Parameters<typeof DOMPurify.sanitize>[1] = {
  ALLOWED_TAGS: [
    'p', 'br',
    'strong', 'b', 'em', 'i', 's', 'strike', 'u', 'mark',
    'h1', 'h2', 'h3', 'h4',
    'ul', 'ol', 'li',
    'blockquote',
    'pre', 'code',
    'span', 'a',
    'img',
    'div', 'label', 'input',
  ],
  ALLOWED_ATTR: [
    'href', 'rel', 'target', 'class', 'src', 'alt', 'title',
    'type', 'checked', 'disabled',
    'data-type', 'data-id', 'data-label', 'data-local-task-id', 'data-checked',
  ],
  ALLOW_DATA_ATTR: false,
};

export function sanitizeDescriptionHtml(html: string): string {
  if (typeof window === 'undefined') return html;
  return DOMPurify.sanitize(html, DESCRIPTION_FROM_MARKDOWN_CONFIG);
}

export function looksLikeMarkdown(text: string): boolean {
  if (!text?.trim()) return false;
  if (text.includes('```')) return true;
  if (/@\[/.test(text)) return true;
  if (text.includes('[Read more](task:')) return true;
  if (/\[[^\]]+\]\([^)]+\)/.test(text)) return true;
  if (/^#{1,4}\s/m.test(text)) return true;
  if (/^>\s/m.test(text)) return true;
  if (/^[ \t]*[-*]\s/m.test(text)) return true;
  if (/^[ \t]*- \[[ xX]\]\s/m.test(text)) return true;
  if (/^[ \t]*\d+\.\s/m.test(text)) return true;
  if (/`[^`\n]+`/.test(text)) return true;
  if (/\*\*[^*]+\*\*/.test(text)) return true;
  if (/\*[^*\n]+\*/.test(text)) return true;
  if (/~~[^~]+~~/.test(text)) return true;
  if (/==[^=]+==/.test(text)) return true;
  if (/\+\+[^+\n]*\+\+/.test(text)) return true;
  return false;
}

export function prefersClipboardHtmlAsRichContent(htmlRaw: string, plain: string): boolean {
  const h = htmlRaw.trim();
  if (!h) return false;
  const p = plain.trim();
  if (p.length < 2) return false;
  const stripped = h
    .replace(/<style[\s\S]*?<\/style>/gi, '')
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/<[^>]+>/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  const hasBlockish = /<(?:p|div|h[1-6]|ul|ol|table|blockquote|section|article)\b/i.test(h);
  if (!hasBlockish && stripped.length < 30) return false;
  if (stripped.length >= p.length * 0.65) return true;
  return false;
}

function escapeAttr(value: string): string {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function escapeHtml(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function processInline(raw: string): string {
  const slots: string[] = [];
  const reserve = (html: string): string => {
    const idx = slots.length;
    slots.push(html);
    return `\x00${idx}\x00`;
  };

  let s = raw;
  s = s.replace(/`([^`\n]+)`/g, (_, code) =>
    reserve(`<code>${escapeHtml(code)}</code>`)
  );
  s = s.replace(/@\[([^\]]+)\]\((\d+)\)/g, (_, label, id) =>
    reserve(
      `<span data-type="mention" data-id="${escapeAttr(id)}" data-label="${escapeAttr(label)}">@${escapeAttr(label)}</span>`
    )
  );
  s = s.replace(/\[Read more\]\(task:(\d+)\)/g, (_, localId) =>
    reserve(
      `<a href="#" class="bugherd-description-read-more-link" rel="noopener noreferrer" data-local-task-id="${escapeAttr(localId)}">Read more</a>`
    )
  );
  s = s.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, (_, alt, url) =>
    reserve(
      `<img src="${escapeAttr(url)}" alt="${escapeAttr(alt)}">`
    )
  );
  s = s.replace(/\[([^\]]*)\]\(([^)]+)\)/g, (_, text, url) =>
    reserve(
      `<a href="${escapeAttr(url)}" target="_blank" rel="noopener noreferrer">${escapeHtml(text)}</a>`
    )
  );
  s = s.replace(/\+\+([\s\S]+?)\+\+/g, (_, inner) =>
    reserve(`<u>${processInline(inner)}</u>`)
  );
  s = s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  s = s.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  s = s.replace(/\*([^*\n]+?)\*/g, '<em>$1</em>');
  s = s.replace(/~~(.+?)~~/g, '<s>$1</s>');
  s = s.replace(/==(.+?)==/g, '<mark>$1</mark>');
  s = s.replace(/\x00(\d+)\x00/g, (_, idx) => slots[parseInt(idx, 10)]);
  return s;
}

type ListKind = 'task' | 'bullet' | 'ordered';

type ListMarker = {
  indent: number;
  kind: ListKind;
  checked: boolean;
  text: string;
};

type ListPart = { kind: 'line'; text: string } | { kind: 'html'; html: string };

function leadingIndent(line: string): number {
  return /^[ \t]*/.exec(line)?.[0].length ?? 0;
}

function parseListMarker(line: string): ListMarker | null {
  const task = /^([ \t]*)- \[([ xX])\] (.*)$/.exec(line);
  if (task) {
    return {
      indent: task[1].length,
      kind: 'task',
      checked: task[2].toLowerCase() === 'x',
      text: task[3]
    };
  }
  const bullet = /^([ \t]*)[-*] (.*)$/.exec(line);
  if (bullet) {
    return { indent: bullet[1].length, kind: 'bullet', checked: false, text: bullet[2] };
  }
  const ordered = /^([ \t]*)\d+\. (.*)$/.exec(line);
  if (ordered) {
    return { indent: ordered[1].length, kind: 'ordered', checked: false, text: ordered[2] };
  }
  return null;
}

function listPartsToHtml(parts: ListPart[]): string {
  let html = '';
  let lines: string[] = [];
  const flush = () => {
    if (!lines.length) return;
    html += `<p>${lines.map(line => processInline(line)).join('<br>')}</p>`;
    lines = [];
  };
  parts.forEach(part => {
    if (part.kind === 'line') {
      lines.push(part.text);
      return;
    }
    flush();
    html += part.html;
  });
  flush();
  return html;
}

function renderListItem(marker: ListMarker, parts: ListPart[]): string {
  const body = listPartsToHtml(parts);
  if (marker.kind === 'task') {
    const checkedAttr = marker.checked ? ' checked' : '';
    return `<li data-type="taskItem" data-checked="${marker.checked}"><label><input type="checkbox"${checkedAttr} disabled="disabled"><span></span></label><div>${body}</div></li>`;
  }
  return `<li>${body}</li>`;
}

function wrapList(kind: ListKind, inner: string): string {
  if (kind === 'task') return `<ul data-type="taskList">${inner}</ul>`;
  if (kind === 'ordered') return `<ol>${inner}</ol>`;
  return `<ul>${inner}</ul>`;
}

function parseList(lines: string[], start: number): { html: string; next: number } {
  const opening = parseListMarker(lines[start]);
  if (!opening) return { html: '', next: start };

  const items: string[] = [];
  let i = start;
  while (i < lines.length) {
    const marker = parseListMarker(lines[i]);
    if (!marker || marker.indent !== opening.indent || marker.kind !== opening.kind) break;

    const parts: ListPart[] = [{ kind: 'line', text: marker.text }];
    i++;
    while (i < lines.length) {
      const line = lines[i];
      if (line.trim() === '') break;
      const nested = parseListMarker(line);
      if (nested && nested.indent > opening.indent) {
        const child = parseList(lines, i);
        parts.push({ kind: 'html', html: child.html });
        i = child.next;
        continue;
      }
      if (nested || leadingIndent(line) <= opening.indent) break;
      parts.push({ kind: 'line', text: line.trim() });
      i++;
    }
    items.push(renderListItem(marker, parts));
  }

  return { html: wrapList(opening.kind, items.join('')), next: i };
}

export function markdownToHtml(description: string): string {
  if (!description || !description.trim()) return '<p></p>';

  const lines = description.split('\n');
  const html: string[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    if (line.trim() === '') {
      let blankCount = 0;
      while (i < lines.length && lines[i].trim() === '') {
        blankCount++;
        i++;
      }
      const extraEmptyParagraphs = Math.max(0, blankCount - 1);
      for (let e = 0; e < extraEmptyParagraphs; e++) {
        html.push('<p></p>');
      }
      continue;
    }

    if (line.trim().startsWith('```')) {
      const lang = line.trim().slice(3).trim();
      const codeLines: string[] = [];
      i++;
      while (i < lines.length && !lines[i].trim().startsWith('```')) {
        codeLines.push(lines[i]);
        i++;
      }
      i++;
      const classAttr = lang ? ` class="language-${escapeAttr(lang)}"` : '';
      html.push(`<pre><code${classAttr}>${escapeHtml(codeLines.join('\n'))}</code></pre>`);
      continue;
    }

    const headingMatch = line.match(/^(#{1,4}) (.*)$/);
    if (headingMatch) {
      const level = headingMatch[1].length;
      html.push(`<h${level}>${processInline(headingMatch[2])}</h${level}>`);
      i++;
      continue;
    }

    if (line.startsWith('> ')) {
      const quoteLines: string[] = [];
      while (i < lines.length && lines[i].startsWith('> ')) {
        quoteLines.push(lines[i].slice(2));
        i++;
      }
      html.push(`<blockquote><p>${quoteLines.map(l => processInline(l)).join('<br>')}</p></blockquote>`);
      continue;
    }

    if (parseListMarker(line)) {
      const parsed = parseList(lines, i);
      if (parsed.html) html.push(parsed.html);
      i = parsed.next;
      continue;
    }

    const paraLines: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim() !== '' &&
      !lines[i].trim().startsWith('```') &&
      !/^#{1,4} /.test(lines[i]) &&
      !lines[i].startsWith('> ') &&
      !/^[-*] /.test(lines[i]) &&
      !/^\d+\. /.test(lines[i])
    ) {
      paraLines.push(lines[i]);
      i++;
    }
    if (paraLines.length > 0) {
      html.push(`<p>${paraLines.map(l => processInline(l)).join('<br>')}</p>`);
    }
  }

  return sanitizeDescriptionHtml(html.join('') || '<p></p>');
}

function stripDocBlockSuffix(md: string): string {
  return md.replace(/\n\n$/, '');
}

function joinTopLevelMarkdownBlocks(
  parts: Array<{ type: 'empty' } | { type: 'md'; s: string }>
): string {
  let result = '';
  let pendingEmpties = 0;
  for (const part of parts) {
    if (part.type === 'empty') {
      pendingEmpties++;
      continue;
    }
    const md = part.s;
    if (result.length > 0) {
      result += '\n'.repeat(pendingEmpties + 2) + md;
    } else if (pendingEmpties > 0) {
      result = '\n'.repeat(pendingEmpties + 1) + md;
    } else {
      result = md;
    }
    pendingEmpties = 0;
  }
  if (pendingEmpties > 0) {
    if (result.length > 0) {
      result += '\n'.repeat(pendingEmpties + 1);
    } else {
      result = '\n'.repeat(pendingEmpties);
    }
  }
  return result;
}

export function htmlToMarkdown(html: string): string {
  if (!html || !html.trim()) return '';

  const temp = document.createElement('div');
  temp.innerHTML = html;

  const WRAP_TAGS: Record<string, string> = {
    strong: '**', b: '**',
    em: '*', i: '*',
    s: '~~', strike: '~~',
    mark: '==',
    u: '++', ins: '++'
  };
  const HEADING_LEVELS: Record<string, number> = { h1: 1, h2: 2, h3: 3, h4: 4 };

  const inner = (el: Element): string => Array.from(el.childNodes).map(walk).join('');

  function ownCheckbox(li: HTMLElement): HTMLInputElement | null {
    for (const child of Array.from(li.children)) {
      const tag = child.tagName.toLowerCase();
      if (tag === 'input' && child.getAttribute('type') === 'checkbox') return child as HTMLInputElement;
      if (tag === 'label') return child.querySelector('input[type="checkbox"]');
    }
    return null;
  }

  function taskItemChecked(li: HTMLElement): boolean {
    const dataChecked = li.getAttribute('data-checked');
    if (dataChecked === 'true') return true;
    if (dataChecked === 'false') return false;
    const input = ownCheckbox(li);
    return !!input && (input.hasAttribute('checked') || input.checked);
  }

  function isTaskListElement(el: HTMLElement): boolean {
    if (el.tagName.toLowerCase() !== 'ul') return false;
    if (el.getAttribute('data-type') === 'taskList') return true;
    return Array.from(el.children).some(child => {
      if (child.tagName.toLowerCase() !== 'li') return false;
      const item = child as HTMLElement;
      return item.getAttribute('data-type') === 'taskItem' || !!ownCheckbox(item);
    });
  }

  function listItemContentElement(li: HTMLElement): HTMLElement {
    if (li.getAttribute('data-type') !== 'taskItem') return li;
    const content = Array.from(li.children).find(child => child.tagName.toLowerCase() === 'div');
    return (content as HTMLElement | undefined) ?? li;
  }

  function markdownLinesFromNode(node: Node): string[] {
    return walk(node)
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0);
  }

  function serializeList(el: HTMLElement, indent: string): string {
    const task = isTaskListElement(el);
    const ordered = el.tagName.toLowerCase() === 'ol';
    const items = Array.from(el.children).filter(child => child.tagName.toLowerCase() === 'li');
    return items.map((item, idx) => {
      const li = item as HTMLElement;
      const marker = task ? `- [${taskItemChecked(li) ? 'x' : ' '}] ` : ordered ? `${idx + 1}. ` : '- ';
      const childIndent = indent + ' '.repeat(marker.length);
      const root = listItemContentElement(li);
      let first = '';
      let hasFirst = false;
      const rest: string[] = [];
      const pushLines = (lines: string[]) => {
        lines.forEach(line => {
          if (!hasFirst) {
            first = line;
            hasFirst = true;
            return;
          }
          rest.push(childIndent + line);
        });
      };

      Array.from(root.childNodes).forEach(child => {
        if (child.nodeType === Node.TEXT_NODE && !(child.textContent || '').trim()) return;
        if (child.nodeType !== Node.ELEMENT_NODE) {
          pushLines(markdownLinesFromNode(child));
          return;
        }
        const childEl = child as HTMLElement;
        const childTag = childEl.tagName.toLowerCase();
        if (childTag === 'label' || childTag === 'input') return;
        if (childTag === 'ul' || childTag === 'ol') {
          hasFirst = true;
          const nested = serializeList(childEl, childIndent);
          if (nested) rest.push(nested);
          return;
        }
        pushLines(markdownLinesFromNode(childEl));
      });

      return [`${indent}${marker}${first}`, ...rest].join('\n');
    }).join('\n');
  }

  const walk = (node: Node): string => {
    if (node.nodeType === Node.TEXT_NODE) return node.textContent || '';
    if (node.nodeType !== Node.ELEMENT_NODE) return '';

    const el = node as HTMLElement;
    const tag = el.tagName.toLowerCase();

    if (el.getAttribute('data-type') === 'mention') {
      const id = el.getAttribute('data-id') || '';
      const label = el.getAttribute('data-label') || el.textContent || '';
      return `@[${label}](${id})`;
    }
    if (tag === 'br') return '\n';
    if (tag === 'img') {
      const src = el.getAttribute('src') || '';
      if (!src) return '';
      const escapedAlt = (el.getAttribute('alt') || '')
        .replace(/\\/g, '\\\\')
        .replace(/\]/g, '\\]');
      return `![${escapedAlt}](${src})`;
    }
    if (tag === 'a') {
      const href = el.getAttribute('href') || '';
      const localTaskId = el.getAttribute('data-local-task-id');
      if (localTaskId) return `[${inner(el)}](task:${localTaskId})`;
      return href ? `[${inner(el)}](${href})` : inner(el);
    }
    if (tag === 'code') {
      if (el.parentElement?.tagName.toLowerCase() === 'pre') return inner(el);
      return `\`${inner(el)}\``;
    }
    if (tag === 'pre') {
      const code = el.querySelector('code');
      const lang = code?.className?.match(/language-(\w+)/)?.[1] ?? '';
      const codeInner = code ? inner(code) : el.textContent || '';
      return `\`\`\`${lang}\n${codeInner}\n\`\`\`\n\n`;
    }
    if (WRAP_TAGS[tag]) {
      const w = WRAP_TAGS[tag];
      return `${w}${inner(el)}${w}`;
    }
    if (HEADING_LEVELS[tag]) {
      return `${'#'.repeat(HEADING_LEVELS[tag])} ${inner(el)}\n\n`;
    }
    if (tag === 'p' || tag === 'div') {
      const paraInner = inner(el);
      if (tag === 'p') {
        const isEmptyish = paraInner === '' || paraInner === '\n' || /^[\s\n]*$/.test(paraInner);
        return isEmptyish ? '\n\n' : `${paraInner}\n\n`;
      }
      return `${paraInner}\n`;
    }
    if (tag === 'ul' || tag === 'ol') return `${serializeList(el, '')}\n\n`;
    if (tag === 'blockquote') return `> ${inner(el).trim()}\n\n`;
    return inner(el);
  };

  const topLevelParts: Array<{ type: 'empty' } | { type: 'md'; s: string }> = [];
  for (const node of Array.from(temp.childNodes)) {
    if (node.nodeType !== Node.ELEMENT_NODE) continue;
    const el = node as HTMLElement;
    const tag = el.tagName.toLowerCase();
    if (tag === 'p') {
      const paraInner = inner(el);
      const isEmptyish = paraInner === '' || paraInner === '\n' || /^[\s\n]*$/.test(paraInner);
      topLevelParts.push(isEmptyish ? { type: 'empty' } : { type: 'md', s: paraInner });
      continue;
    }
    topLevelParts.push({ type: 'md', s: stripDocBlockSuffix(walk(el)) });
  }

  return joinTopLevelMarkdownBlocks(topLevelParts);
}
